// "Add notes" (module 10 U209; pipeline I2, REQ-2026-10-04-31): pick one file or many, in any format, and each goes up
// in turn, leaving a line that says what it came to. On a meeting the notes are that meeting's, on a company or a deal
// card that company's or deal's (REQ-2026-10-08-13); on the Deals head they are nobody's in particular until their words
// name a company.
import { ActionInput, ActionButton } from '../parts/ActionButton.js';
import { useRef, useState, type ReactNode } from 'react';
import { NOTE_ACCEPT, notesApi } from './api.js';
import { uploadedWords } from './words.js';
import './notes.css';

type Line = { key: string; text: string; tone: 'plain' | 'warn' };

export function AddNotes({ store, meetingId = null, companyId = null, dealId = null, onAdded, beside = null }: {
  store: string; meetingId?: string | null; companyId?: string | null; dealId?: string | null; onAdded?: () => void;
  /** Drawn on the button's line, before what the files came to (the Deals head puts Your notes there). */
  beside?: ReactNode;
}) {
  const input = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState<{ at: number; of: number } | null>(null);
  const [lines, setLines] = useState<Line[]>([]);

  const send = async (files: File[]) => {
    if (files.length === 0) return;
    setLines([]);
    const said: Line[] = [];
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i]!;
      setBusy({ at: i + 1, of: files.length });
      let w: { text: string; tone: 'plain' | 'warn' };
      try {
        w = uploadedWords(f.name, await notesApi.upload(store, f, { meeting: meetingId, company: companyId, deal: dealId }));
      } catch {
        w = { text: `${f.name}: it was not uploaded. Try again.`, tone: 'warn' };
      }
      said.push({ key: `${i}-${f.name}`, ...w });
      setLines([...said]);
    }
    setBusy(null);
    onAdded?.();
  };

  return (
    <div className="nt-add">
      <ActionInput
        ref={input} type="file" multiple accept={NOTE_ACCEPT} hidden
        onChange={(e) => { const fs = Array.from(e.target.files ?? []); e.target.value = ''; void send(fs); }}
      />
      <ActionButton type="button" className="nt-go" disabled={busy !== null} onClick={() => input.current?.click()}>
        {busy ? (busy.of > 1 ? `Adding ${busy.at} of ${busy.of}…` : 'Adding…') : 'Add notes'}
      </ActionButton>
      {beside}
      {lines.length > 0
        ? (
          <ul className="nt-said" role="status">
            {lines.map((l) => <li key={l.key} className={l.tone === 'warn' ? 'warn' : undefined}>{l.text}</li>)}
          </ul>
        )
        : null}
    </div>
  );
}
